import { observable } from "mobx";
import { ArrayNotEmpty, IsNotEmpty, ValidateNested } from "class-validator";
import { classToPlain, plainToClass } from "class-transformer";
import locale from "@bonliva-crm/locale";
import { createObjectID } from "@bonliva-crm/common";
import {
  ContractPriceTemplateInput as IContractPriceTemplateInput,
  ContractPriceTemplateRecordInput as IContractPriceTemplateRecordInput,
  ContractsPriceTemplateFragment,
  Currency,
  PayPeriod,
  PayUnit,
  PriceListDay,
  PriceListWorkType,
  ContractsPriceTemplateType,
  HighHolidayInput
} from "@graphql";
import { store } from "@store";

export class ContractPriceTemplateRecordInput
  implements IContractPriceTemplateRecordInput
{
  static CreateBlank(): ContractPriceTemplateRecordInput {
    const item = new ContractPriceTemplateRecordInput();

    item._id = createObjectID();
    item.id = item._id;
    item.articleNumber = null;
    item.articleNumberForOtherSpeciality = null;
    item.paidPer = PayUnit.Hour;
    item.currency = Currency.SEK;
    item.workType = PriceListWorkType.Active;
    item.period = PayPeriod.Daily;
    item.priceMultiplier = 1;
    item.fixedPrice = null;
    item.startDay = PriceListDay.BusinessDay;
    item.from = "06:00";
    item.endDay = null;
    item.to = "19:00";
    item.isSecondPass = null;

    return item;
  }

  static CreateBlankBC(): ContractPriceTemplateRecordInput {
    const item = new ContractPriceTemplateRecordInput();

    item._id = createObjectID();
    item.id = item._id;
    item.articleNumber = null;
    item.articleNumberForOtherSpeciality = null;
    item.paidPer = PayUnit.Investigation;
    item.currency = Currency.SEK;
    item.workType = PriceListWorkType.Active;
    item.period = PayPeriod.Daily;
    item.priceMultiplier = 1;
    item.fixedPrice = null;
    item.startDay = PriceListDay.BusinessDay;
    item.from = "06:00";
    item.endDay = null;
    item.to = "19:00";
    item.isSecondPass = null;

    return item;
  }

  @observable
  _id: string;

  // trick for timereport service => validate options
  id: string;

  // @IsEnum(PriceListDay, { message: "*" })
  @observable
  endDay: PriceListDay = null;

  // @IsNotEmpty({ message: "*" })
  @observable
  articleNumber: string = null;

  @observable
  articleNumberForOtherSpeciality: string = null;

  @observable
  from: string;

  // @IsEnum(PayUnit)
  @observable
  paidPer: PayUnit;

  // @IsEnum(PayPeriod)
  @observable
  period: PayPeriod;

  @observable
  priceMultiplier: number;

  @observable
  fixedPrice: number;

  @observable
  customPrice: number;

  // @IsEnum(Currency)
  @observable
  currency: Currency;

  // @IsEnum(PriceListDay)
  @observable
  startDay: PriceListDay;

  // @IsNotEmpty({ message: "*" })
  @observable
  to: string;

  // @IsEnum(PriceListWorkType)
  @observable
  workType: PriceListWorkType;

  @observable
  indexInWorkType: number;

  @observable
  deleted: boolean = false;

  @observable
  isFreeInput?: boolean = false;

  @observable
  isSecondPass?: boolean = false;
}

export class ContractPriceTemplateFormInput implements IContractPriceTemplateInput {
  static CreateFromFragment(data: ContractsPriceTemplateFragment) {
    const item = plainToClass(ContractPriceTemplateFormInput, data, {
      excludePrefixes: ["__"]
    });

    return item;
  }

  getPlain(): IContractPriceTemplateInput {
    const plain = classToPlain(this, { excludePrefixes: ["__"] });

    plain.records = plain.records.map((x) => ({
      _id: x._id,
      articleNumber: x.articleNumber || null,
      articleNumberForOtherSpeciality: x.articleNumberForOtherSpeciality || null,
      paidPer: x.paidPer || null,
      currency: x.currency || null,
      workType: x.workType || null,
      period: x.period || null,
      from: x.from || null,
      to: x.to || null,
      endDay: x.endDay || null,
      startDay: x.startDay || null,
      priceMultiplier: parseFloat(x.priceMultiplier),
      fixedPrice: parseFloat(x.fixedPrice),
      customPrice: parseFloat(x.customPrice),
      name: x.name,
      deleted: x.deleted,
      indexInWorkType: x.indexInWorkType
    }));

    return plain as any;
  }

  @IsNotEmpty({ message: locale.Title })
  @observable
  name: string = "";

  @observable
  type: ContractsPriceTemplateType = store().user.can(
    "contracts-price-template:create:all"
  )
    ? null
    : ContractsPriceTemplateType.ConsultantContract;

  @observable
  private = !store().user.can("contracts-price-template:create:all");

  @ArrayNotEmpty({ message: locale.Required })
  @ValidateNested()
  @observable
  records: ContractPriceTemplateRecordInput[] = [
    ContractPriceTemplateRecordInput.CreateBlank()
  ];

  @observable
  highHolidays: HighHolidayInput[] = [];

  @observable
  deleted: boolean = false;
}
